import { Container, Dialog, DialogContent, Divider, Grid, IconButton, Typography } from '@material-ui/core';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import {
    Add as AddIcon, BackspaceOutlined as BackspaceOutlinedIcon,
    Delete as DeleteIcon, Search as SearchIcon
} from '@material-ui/icons';
import MUIDataTable from "mui-datatables";
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import TextField from '../../components/Form/TextField';
import AlertDialog from '../../components/ui/AlertDialog';
import ButtonUI from '../../components/ui/ButtonUI';
import LoadingUI from '../../components/ui/LoadingUI';
import PageTitle from '../../components/ui/PageTitleUI';
import { useAuth } from '../../hooks/auth';
import MovementTypeApi from '../../services/MovementTypeApi';
import getDefaultGridOptions from '../../utils/componentUtils';
import permissions from '../../utils/Permissions';
import useKeypress from '../../utils/useKeypress';
import MovementTypeForm from './MovementTypeForm';
import { DialogTitle, PaperComponent, useStyles } from './Style';

interface IFormInput {
    id: any;
    description: any;
}

const opcoes = [
    { url: '/MovementType', descricao: 'Incluir', state: {openPanelOnLoad: true}},
    { url: '/MovementType', descricao: 'Pesquisar', state: {}}
]

interface Params {
    openPanelOnLoad: boolean;
}

const MovementType: React.FC = () => {
    const classes = useStyles();
    const location = useLocation<Params>();
    const [movementTypes, setMovementTypes] = useState<any>();
    const [movementTypeDescription, setMovementTypeDescription] = useState<any>();
    const [movementTypeId, setMovementTypeId] = useState<any>();
    const [loading, setLoading] = useState(true);
    const [openedPainel, setOpenedPainel] = React.useState(false);
    const { user, hasPermission } = useAuth();
    const { t } = useTranslation("MovementType");
    const { t: _t } = useTranslation("General");
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();
    const { control, handleSubmit, reset, getValues } = useForm<IFormInput>(
        {
            defaultValues: { description: '' }
        }
    )
    const [success, setSuccess] = useState(false);
    
    const searchMovementTypes = (data: any) => {
        setLoading(true)
        data.PlantId = user.company.plantID;
        data.pageSize = 1000
        data.showDeleted = false
        MovementTypeApi.searchMovementType(data)
            .then(result => {
                if (result) {
                    setMovementTypes(result)
                }
                else {
                    setMovementTypes([])
                }
            }).catch(() => {
                setMovementTypes([])
            }).finally(() => {
                setLoading(false)
            });
    }

    useEffect(() => {
        if (!hasPermission(permissions.MOVEMENT_TYPE.Search))
            history.replace('NoPermission');
        
        setMovementTypes([])
        searchMovementTypes(getValues())
    }, [history, user]);

    const onSubmit = (data: any) => {
        searchMovementTypes(data)
    }

    const columns = [
        {
            name: 'id',
            label: String(t('code')),   
            options: {
                sort: true,
                setCellProps: () => ({
                    align: "left"
                }),                
            }
        },
        {
            name: 'description',
            label: String(t('description')),
            
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    align: "left",
                  }),
                setCellProps: () => ({
                    align: "left"
                }), 
            }
        },
        {
            name: "",
            options: {
                setCellProps: () => ({
                    align: "right"
                }),
                disableClick: true,
                sort: false,
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => (
                    renderButtons(tableMeta.rowData[0])
                )
            }
        }
    ]

    const renderButtons = (id: number) => {
        return (
            <AlertDialog
                dialogTitle={t("dialogDeleteTitle")}
                dialogContentText={t("dialogDeleteContentText")}
                cancelTextButton={t("cancelDeleteTextButton")}
                confirmTextButton={t("confirmDeleteTextButton")}
                confirmDialogFunction={() => { handleDeleteItem(id); }}
                Button={
                    <IconButton size='small' edge="end"  disabled={!hasPermission(permissions.MOVEMENT_TYPE.Delete)}>
                        <DeleteIcon />
                    </IconButton>
                }
            />
        );
    }

    function handleDeleteItem(id: number) {
        setLoading(true);
        MovementTypeApi.deleteMovementType(id)
            .then(result => {
                enqueueSnackbar(t("returnSubmitDeleteFormSuccess"), { variant: 'success' });
            }).finally(() => {
                searchMovementTypes(getValues())
            });
    }

    useKeypress('Escape', () => {
        setOpenedPainel(false);
    });

    const handleOpenPanelChange = (id: number = 0, description: string = "") => {
        setMovementTypeId(id);
        setMovementTypeDescription(description);
        setOpenedPainel(true);
    };

    const callbackDialogHandle = () => {
        setOpenedPainel(false);
        searchMovementTypes(getValues())
    }

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: movementTypes ? 'pointer' : ''                    
                }
            }
        }
    })

    useEffect(() => {
        if (location.state?.openPanelOnLoad)
        {
            handleOpenPanelChange();
            setMovementTypeDescription('');
        }
    }, [location.state?.openPanelOnLoad])
    
    return (
        <Container maxWidth={false} component="main">

            <Dialog
                open={openedPainel}
                aria-labelledby="form-dialog-title"
                PaperComponent={PaperComponent}
                onBackdropClick={() => setOpenedPainel(false)}
                fullWidth>

                <DialogTitle classes={{ root: classes.dialogTitle }}
                    id="draggable-dialog-title"
                    onClose={() => setOpenedPainel(false)}>
                    {movementTypeDescription !== '' ? t("editTitle") : t("addTitle")}
                </DialogTitle>
                <DialogContent>
                    <MovementTypeForm description={movementTypeDescription} id={movementTypeId} handleSetSuccess={setSuccess} clickHandlerCalbkack={callbackDialogHandle} clickHandlerClose={() => setOpenedPainel(false)} />
                </DialogContent>

            </Dialog>

            <PageTitle title={t("movementType")} />
            <div className={classes.rootContent}>
                <div className={classes.paper}>

                    <form onSubmit={handleSubmit(onSubmit)} >
                        <Grid container spacing={1}>
                            <Grid item xs={6} style={{ padding: "0px", display: 'flex', alignItems: 'center' }}>
                                <Typography className={classes.root}>
                                    {t("titleSearch")}
                                </Typography>
                            </Grid>

                            <Grid item xs={6} style={{ padding: "0px", textAlign: 'right' }}>
                                <ButtonUI disabled={!hasPermission(permissions.MOVEMENT_TYPE.Add)} color="secondary" onClick={() => { handleOpenPanelChange(); setMovementTypeDescription('') }} startIcon={<AddIcon />}>{t("add")}</ButtonUI>
                                <ButtonUI onClick={() => reset()} startIcon={<BackspaceOutlinedIcon />}>{t("clean")}</ButtonUI>
                                <ButtonUI type="submit" color="primary" startIcon={<SearchIcon />}>{t("search")}</ButtonUI>
                            </Grid>
                        </Grid>

                        <Divider style={{ marginTop: '10px' }} />

                        <Grid container spacing={1}>
                            <Grid item xs={2}>
                                <TextField 
                                    label={t("description")} 
                                    name="description" 
                                    control={control} 
                                    fullWidth/> 
                            </Grid>
                        </Grid>
                    </form>

                    {
                        loading ? <LoadingUI /> :
                            <MuiThemeProvider theme={myTheme}>
                                <MUIDataTable
                                    title=""
                                    data={movementTypes.items}
                                    columns={columns}                                        
                                    options={{
                                        ...getDefaultGridOptions(_t),
                                        serverSide: false,
                                        pagination: movementTypes ? true : false,
                                        sort: movementTypes ? true : false,
                                        selectToolbarPlacement: "none",
                                        filter: false,
                                        search: false,
                                        download: false,
                                        print: false,
                                        viewColumns: false,
                                        selectableRows: 'none',
                                        onCellClick: (_colData, { colIndex, dataIndex }) => {
                                            if (hasPermission(permissions.MOVEMENT_TYPE.Edit))
                                                if ((movementTypes && movementTypes.items) && colIndex < 2) {
                                                    handleOpenPanelChange(movementTypes.items[dataIndex].id, movementTypes.items[dataIndex].description)
                                                }
                                        },
                                    }}
                                />
                            </MuiThemeProvider>
                    }
                </div>
            </div>
        </Container>
    )
}
export default MovementType;
